import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { refillRequestDeliveryTypes } from '../../../../config/app.config';
import { FooterButtons } from '../../../../shared/components/Modals/FooterButtons';

import {
  useRefillRequestModalContext,
  getRefillRequestsHeaders,
  useStatusChangeModalContext,
} from '../utils';
import {
  AddressField,
  StatusHistoryTable,
  StatusDropdown,
  getFilteredStatuses,
} from '../Components';
import { useGetRefillRequestMessages } from '../../../../hooks/refill-requests/useGetRefillRequestMessages';
import responsiveHOC from "react-lines-ellipsis/lib/responsiveHOC";
import LinesEllipsis from "react-lines-ellipsis";
import { useNestedModal } from "../../../modals/AppointmentStatusesArchive/AppointmentStatusesArchive.hooks";
import { TextDisplayModal } from "../../../modals/ContentModals";

const ResponsiveEllipsis = responsiveHOC()(LinesEllipsis);

const excludedKeys = [
  'date',
  'time',
  'attachments',
  'internalComments',
  'message',
  'messageStatus',
  'copyHistory',
];

const additionalFields = [
  { key: 'address', name: 'Shipping Address' },
  { key: 'dateTime', name: 'Date and Time' },
];

const stickyKeys = ['dateTime', 'status', 'deliveryType', 'address'];

export function MainInfoTab() {
const messageModal = useNestedModal();

  const { closeRefillRequestModal, refillRequestData } =
    useRefillRequestModalContext();
  const { prepareConfirmationMessage } = useStatusChangeModalContext();

  const { data: messages = [] } = useGetRefillRequestMessages(
    { refillRequestId: refillRequestData.id },
    {
      enabled: !!refillRequestData.id,
    },
  );

  const heads = useMemo(() => {
    return getRefillRequestsHeaders()
      .filter(({ key }) => !excludedKeys.includes(key))
      .concat(additionalFields);
  }, []);

  const stickyHeads = useMemo(
    () => heads.filter((h) => stickyKeys.includes(h.key)),
    [heads],
  );

  const scrollableHeads = useMemo(
    () => heads.filter((h) => !stickyKeys.includes(h.key)),
    [heads],
  );

  const allStatuses = useSelector(
    (state) => state.refillRequests.refillRequestStatuses || [],
  );
  const dropdownStatuses = getFilteredStatuses(
    refillRequestData.deliveryType,
    allStatuses,
  );

  const isPickupDelivery = refillRequestData.deliveryType === 'pickup';

  const handleConfirmationModalOpen = (newStatus) => {
    prepareConfirmationMessage({
      currentStatus: refillRequestData.status,
      newStatus: newStatus.label,
      newStatusId: newStatus._id,
      prescriptionId: refillRequestData.id,
    });
  };

  const renderField = ({
    key,
    name,
    capitalize,
    display,
    link: linkToProfile,
  }) => {
    const { zipCode, state, city, address } = refillRequestData;

    let dataToShow = refillRequestData[key];
    const link = linkToProfile?.(refillRequestData);
    let addEllipsisClass = !link; // for link ellipsis class is added to anchor tag

    if (key === 'deliveryType' && dataToShow === 'delivery') {
      dataToShow = refillRequestDeliveryTypes.delivery;
    }

    if (key === 'address') {
      return (
        <AddressField key={key} addressData={[address, city, state, zipCode]} />
      );
    }

    if (key === 'comments') {
      addEllipsisClass = false;
      name = 'Client Comments';
      dataToShow = dataToShow.trim() || 'No comments provided';

      return (
          <div
              key={key}
              className={`${key} modal-main-info-field `}
              style={{ gridArea: key, display: display ?? 'block' }}
          >
              <p className="field-label">{name}</p>
              <div
                  style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                  className={classNames('field-data', {
                    'text-capitalize': capitalize,
                    ellipsis: addEllipsisClass,
                })}
              >
                  <ResponsiveEllipsis
                      text={dataToShow}
                      maxLine={5}
                      ellipsis={
                          <span>
                            ...{' '}
                              <button
                                  type="button"
                                  className="nav-link-style"
                                  onClick={() => messageModal.open(dataToShow)}
                              >
                          View
                        </button>
                      </span>
                      }
                      trimRight
                      basedOn="letters"
                  />
              </div>
          </div>
      );
    }

    if (key === 'status') {
      return (
        <div
          key={key}
          className="status modal-main-info-field"
          style={{ gridArea: 'status' }}
        >
          <p className="field-label">{name}</p>
          <StatusDropdown
            currentStatus={refillRequestData.status}
            statuses={dropdownStatuses}
            onStatusChange={handleConfirmationModalOpen}
            className="field-data text-capitalize"
          />
        </div>
      );
    }

    return (
      <div
        key={key}
        className={`${key} modal-main-info-field `}
        style={{ gridArea: key, display: display ?? 'block' }}
      >
        <p className="field-label">{name}</p>
        <p
          className={classNames('field-data', {
            'text-capitalize': capitalize,
            ellipsis: addEllipsisClass,
          })}
          title={dataToShow}
        >
          {link ? (
            <Link to={link} className="ellipsis">
              {dataToShow}
            </Link>
          ) : (
            dataToShow
          )}
        </p>
      </div>
    );
  };

  const renderFields = (fields) => fields.map(renderField);

  return (
    <>
      <section className="main-info-tab">
        <div className="scrollable-modal-area no-gap">
          <div className="sticky-fields">{renderFields(stickyHeads)}</div>
          <div
            className={classNames('modal-main-info-data', {
              'no-address': isPickupDelivery,
            })}
          >
            {renderFields(scrollableHeads)}
          </div>

<StatusHistoryTable
  messages={messages}
  header={{
    parent: refillRequestData.parentFullName,
    pet: refillRequestData.petName,
    doctor: refillRequestData.doctorFullName,
    medication: refillRequestData.medication,
    quantity: refillRequestData.quantity,
    dosage: refillRequestData.dosage,
    submitted: refillRequestData.dateTime,
  }}
/>
        </div>
      </section>

      <FooterButtons onClose={closeRefillRequestModal} showSaveButton={false} />

        <TextDisplayModal
            modalClassName="centered"
            isOpen={messageModal.isOpen}
            onClose={messageModal.close}
            title="Message"
            content={messageModal?.record || ''}
        />
    </>
  );
}
